import React, {useContext, useEffect, useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Button, ButtonGroup, Stack, Typography} from "@mui/material";
import AuthContext from "../Services/AuthContext";
import Main from "../Layout/Main";
import {
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow
} from "@fluentui/react-components";
import {useNavigate, useParams} from "react-router-dom";
import {Check, ContactPage, Delete, ExpandMore, FileDownload, Print} from "@mui/icons-material";
import {CSVDownloader} from "react-papaparse";

function CampRegistrations() {
    const [user] = useContext(AuthContext);
    const params = useParams();
    const [items, setItems] = useState([]);
    const [camp, setCamp] = useState({});
    const [refresh, setRefresh] = useState(0);
    const navigate = useNavigate();
    useEffect(() => {
        if (user.token === "") {
            navigate("/");
        }
    }, [user.token, navigate]);

    const _payment = (camp, children, method = 'POST') => {
             fetch(`/api/camp/${camp}/paid/${children}`, {
                method: method,
                headers: {
                    'Authorization': 'Bearer ' + user.token
                }
            }).then(res => res.json())
                .then(data => {
                    setRefresh(r => ++r);
                })
                .catch(err => {
                    console.log(err)
                })
    };

    const _cancelPayment = (camp, children) => {
        _payment(camp, children, 'DELETE');
    };

    const _delete = (id) => {
        fetch(`/api/unregister/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': 'Bearer ' + user.token
            }
        }).then(res => res.json())
            .then(data => {
                setRefresh(r => ++r);
            })
            .catch(err => {
                console.log(err)
            })
    }

    const _restore = (id) => {
        fetch(`/api/registration/${id}/restore`, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + user.token
            }
        }).then(res => res.json())
            .then(data => {
                setRefresh(r => ++r);
            })
            .catch(err => {
                console.log(err)
            })
    }

    useEffect(() => {
        const abortController = new AbortController();
        async function fetchData() {
            await fetch(`/api/camp/${params.id}/registrations`, {
                method: 'GET',
                signal: abortController.signal,
                headers: {
                    'Authorization': 'Bearer ' + user.token
                }
            }).then(res => res.json())
                .then(data => {
                    setItems(data);
                })
                .catch(err => {
                    console.log(err)
                })
        }
        async function fetchHeader() {
            await fetch(`/api/global/camp/${params.id}`, {
                method: 'GET',
                signal: abortController.signal,
            }).then(res => res.json())
                .then(data => {
                    setCamp(data);
                })
                .catch(err => {
                    console.log(err)
                })
        }

        fetchHeader();
        fetchData();
        return () => abortController.abort();
    }, [refresh, params.id, user.token]);

    const columns = [
        createTableColumn ({
            columnId: "child",
            renderHeaderCell: () => {
                return "Dziecko";
            },
            renderCell: (item) => {
                let documentLink = '';
                if (item?.document !== null && item.document !== undefined) {
                    documentLink = item.document.split(',').map(doc => <Button key={doc} target={'_blank'} href={`/api/children/document/${doc}?auth=${user.token}`} variant={'contained'} color={'info'}><ContactPage /></Button>);
                }
                const lastDocument = documentLink !== '' ? documentLink.pop() : '';
                let agreementLink = '';
                if (item?.agreements !== null && item.agreements !== undefined) {
                    agreementLink = item.agreements.split(',').map(doc => <Button key={doc} target={'_blank'} href={`/api/children/agreement/${doc}/file?auth=${user.token}`} variant={'contained'} color={'success'}><Check /></Button>);
                }
                let hidden = '';
                if (documentLink.length > 0) {
                    hidden = <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls="Archiwalne wnioski"
                        >
                            Archiwalne wnioski
                        </AccordionSummary>
                        <AccordionDetails>
                            {documentLink}
                        </AccordionDetails>
                    </Accordion>;
                }
                return <span>{hidden}<h3>{item.name} {item.surname}</h3> {lastDocument} {agreementLink}</span>;
            }
        }),
        createTableColumn ({
            columnId: "adress",
            renderHeaderCell: () => {
                return "Adres";
            },
            renderCell: (item) => {
                return `${item.city}, ${item.street}`;
            }
        }),
        createTableColumn ({
            columnId: "guardian",
            renderHeaderCell: () => {
                return "Opiekun";
            },
            renderCell: (item) => {
                const guardians = item.guardians.split('###').map(guardian => <div>{guardian}</div>);
                return <Stack vertical sx={{textAlign: 'left'}}>{guardians}</Stack>
            }
        }),
        createTableColumn ({
            columnId: "phone",
            renderHeaderCell: () => {
                return "Telefon";
            },
            renderCell: (item) => {
                const guardians = item.phone.split('###').map(guardian => <div>{guardian}</div>);
                return <Stack vertical sx={{textAlign: 'left'}}>{guardians}</Stack>
            }
        }),
        createTableColumn ({
            columnId: "email",
            renderHeaderCell: () => {
                return "Email";
            },
            renderCell: (item) => {
                const guardians = item.guardian_email.split('###').map(guardian => <div>{guardian}</div>);
                return <Stack vertical sx={{textAlign: 'left'}}>{guardians}</Stack>
            }
        }),
        createTableColumn ({
            columnId: "reservation",
            renderHeaderCell: () => {
                return "Status";
            },
            renderCell: (item) => {
                if (item.deleted) {
                    return <Button fullWidth title={item.deleted} onDoubleClick={() => _restore(item.id)} variant={'contained'} color={'error'}>Usunięty</Button>
                }
                if (item.confirmed) {
                    if (item.paid) {
                        return <Button fullWidth title={item.confirmed} onDoubleClick={() => _cancelPayment(item.camp_id, item.children_id)} variant={'contained'} color={'success'}>Zawarta Umowa</Button>
                    }
                    if (new Date(item.registration_end) < new Date()) {
                        return <ButtonGroup>
                            <Button fullWidth title={item.confirmed} onClick={() => _payment(item.camp_id, item.children_id)} variant={'outlined'} color={'primary'}>Potwierdzony</Button>
                            <Button variant={'contained'} color={'error'} onDoubleClick={() => _delete(item.id)}><Delete /></Button>
                        </ButtonGroup>;
                    }
                    return <Button fullWidth title={item.confirmed} onClick={() => _payment(item.camp_id, item.children_id)} variant={'contained'} color={'primary'}>Potwierdzony</Button>
                } else {
                    return <ButtonGroup><Button fullWidth title={item.created} variant={'contained'} color={'warning'}>Rezerwowy</Button><Button variant={'contained'} color={'error'} onDoubleClick={() => _delete(item.id)}><Delete /></Button>
                    </ButtonGroup>;
                }
            }
        }),
    ];
    return (
        <Main title={`Uczestnicy ${camp.camp}`} actions={<>
            <Typography variant={'h5'}>{camp.season}: {camp.organizer}</Typography>
            <Typography><strong>{camp.start_date}</strong> ➡ {camp.end_date}</Typography>
            <Button className={'print'} variant={'outlined'} onClick={() => window.print()}><Print /></Button>
            <CSVDownloader data={items} filename={`zapisy`} bom={true}
                           config={{delimiter: ';'}}>
                <Button className={'print'} variant={'outlined'}><FileDownload/> CSV</Button>
            </CSVDownloader>
        </>}>
            <DataGrid
                items={items}
                columns={columns}
                sortable
                getRowId={(item) => item.id}
                focusMode="composite"
                style={{minWidth: "95vw"}}
            >
                <DataGridHeader>
                    <DataGridRow>
                        {({renderHeaderCell}) => (
                            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                    {({item, rowId}) => (
                        <DataGridRow key={rowId}>
                            {({renderCell}) => (
                                <DataGridCell>{renderCell(item)}</DataGridCell>
                            )}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>
        </Main>
    );
}

export default CampRegistrations;